import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { ShieldCheck, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

const STATUSES = ["all", "pending", "approved", "under_review", "hold", "docs_requested", "rejected", "suspended"];
const COLORS = {
  approved: "bg-green-500", pending: "bg-yellow-500", under_review: "bg-blue-500",
  hold: "bg-yellow-500", docs_requested: "bg-yellow-500",
  rejected: "bg-red-500", suspended: "bg-neutral-800",
};

export default function AdminEnterprise() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [c, a] = await Promise.all([
        api.get("/admin/candidates", { params: { status: filter === "all" ? undefined : filter, q: q || undefined } }),
        api.get("/admin/analytics"),
      ]);
      setCandidates(c.data.candidates || []);
      setAnalytics(a.data);
    })().catch(() => {});
  }, [filter, q]);

  const t = analytics?.totals || {};

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8" data-testid="admin-enterprise-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-brand-terracotta flex items-center gap-2"><ShieldCheck size={14} weight="fill" /> Verification console</div>
          <h1 className="font-serif text-5xl mt-2 tracking-tight text-brand-ink">Candidates</h1>
          <div className="text-sm text-brand-muted mt-1">Signed in as <strong className="text-brand-ink">{user?.email}</strong> · role <strong className="text-brand-terracotta">{user?.role}</strong></div>
        </div>
        <div className="text-xs text-brand-muted uppercase tracking-widest">Last 24h actions · <span className="text-brand-ink font-semibold">{analytics?.last_24h_actions ?? 0}</span></div>
      </div>

      {/* Analytics tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          ["total", "Total", "bg-brand-ink text-white"],
          ["approved", "Approved", "bg-green-600 text-white"],
          ["pending", "Pending", "bg-yellow-500 text-white"],
          ["under_review", "Review", "bg-blue-600 text-white"],
          ["hold", "On hold", "bg-orange-500 text-white"],
          ["docs_requested", "Docs", "bg-yellow-600 text-white"],
          ["rejected", "Rejected", "bg-red-600 text-white"],
          ["suspended", "Suspended", "bg-neutral-900 text-white"],
        ].map(([k, label, cls]) => (
          <button key={k} onClick={() => setFilter(k === "total" ? "all" : k)} className={`${cls} p-4 text-left hover:opacity-90 transition-opacity`} data-testid={`analytics-${k}`}>
            <div className="font-serif text-3xl tracking-tighter">{t[k] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">{label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 rounded-none border-brand-line" data-testid="admin-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full ${filter === s ? "bg-brand-ink text-white" : "border border-brand-line text-brand-muted hover:text-brand-ink"}`} data-testid={`filter-${s}`}>
              {s === "all" ? "All" : s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate table */}
      <div className="mt-4 bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bone">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Profile</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Candidate</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Plan</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Completion</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">AI</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Badge</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-brand-muted">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id} className="border-t border-brand-line hover:bg-brand-bone/40" data-testid={`candidate-row-${c.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-brand-muted">{c.profile_id}</td>
                <td className="px-4 py-3">
                  <div className="text-brand-ink font-medium">{c.name}</div>
                  <div className="text-xs text-brand-muted">{c.email} · {c.gender} · {c.age || "?"}</div>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-widest">{c.plan}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-brand-line rounded-full overflow-hidden"><div className="h-full bg-brand-terracotta" style={{ width: `${c.completeness}%` }} /></div>
                    <span className="text-xs">{c.completeness}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">{c.ai_score ? `${c.ai_score}/100` : "—"}</td>
                <td className="px-4 py-3">{c.badge ? <span className="text-[10px] uppercase tracking-widest text-brand-terracotta">✓ Awarded</span> : <span className="text-[10px] uppercase tracking-widest text-brand-muted">Not yet</span>}</td>
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest text-white ${COLORS[c.status] || "bg-neutral-400"}`}><span className="w-1 h-1 rounded-full bg-white/80" />{c.status.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-right"><Link to={`/admin/candidate/${c.id}`} className="text-xs uppercase tracking-widest text-brand-terracotta hover:underline inline-flex items-center gap-1" data-testid={`open-${c.id}`}>Open <ArrowRight size={12} /></Link></td>
              </tr>
            ))}
            {candidates.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-brand-muted italic">No candidates match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
